type EventAttributes = Record<string, string>;

// Access Faro via the global it registers on window (defaultGlobalObjectKey = 'faro').
// This avoids a static import of @grafana/faro-react which would pull the entire SDK
// into the main bundle even when Faro is not initialized (e.g. on localhost/CI).
function getFaroApi() {
  if (typeof window === "undefined") return undefined;
  return (window as Window & { faro?: { api?: { pushEvent: (name: string, attrs?: EventAttributes) => void } } }).faro?.api;
}

/**
 * Convert values to strings for Faro compatibility
 */
function toStringAttributes(
  attrs?: Record<string, string | number | boolean>
): EventAttributes | undefined {
  if (!attrs) return undefined;
  const result: EventAttributes = {};
  for (const [key, value] of Object.entries(attrs)) {
    result[key] = String(value);
  }
  return result;
}

/**
 * Track a custom event in Grafana Faro
 * Only sends events in production to avoid polluting analytics
 */
function trackEvent(
  name: string,
  attributes?: Record<string, string | number | boolean>
) {
  if (process.env.NODE_ENV === "development") {
    console.debug("[Analytics]", name, attributes);
    return;
  }

  try {
    getFaroApi()?.pushEvent(name, toStringAttributes(attributes));
  } catch {
    // Faro not initialized, ignore
  }
}

/**
 * Theme change events
 */
export function trackThemeChange(
  themeType: "brightness" | "design" | "animation",
  value: string,
  previousValue?: string
) {
  trackEvent("theme_change", {
    theme_type: themeType,
    new_value: value,
    ...(previousValue && { previous_value: previousValue }),
  });
}

/**
 * Navigation events
 */
export function trackNavigation(to: string, from?: string) {
  trackEvent("navigation", {
    to_path: to,
    ...(from && { from_path: from }),
  });
}

/**
 * Scroll depth tracking
 */
export function trackScrollDepth(depth: number, page: string) {
  trackEvent("scroll_depth", {
    depth_percent: depth,
    page,
  });
}

/**
 * CTA and button click tracking
 */
export function trackClick(
  element: string,
  context?: string,
  attributes?: Record<string, string | number | boolean>
) {
  trackEvent("click", {
    element,
    ...(context && { context }),
    ...attributes,
  });
}

/**
 * Infinite scroll pagination tracking
 */
export function trackPagination(
  page: number,
  totalLoaded: number,
  contentType: "blog" | "projects"
) {
  trackEvent("pagination_load", {
    page,
    total_loaded: totalLoaded,
    content_type: contentType,
  });
}

/**
 * External link clicks
 */
export function trackExternalLink(url: string, context?: string) {
  trackEvent("external_link", {
    url,
    ...(context && { context }),
  });
}

/**
 * Error tracking (supplement to automatic error tracking)
 */
export function trackError(
  errorType: string,
  message: string,
  context?: string
) {
  trackEvent("custom_error", {
    error_type: errorType,
    message,
    ...(context && { context }),
  });
}

/**
 * Performance marks for custom timing
 */
export function trackTiming(name: string, durationMs: number) {
  trackEvent("timing", {
    name,
    duration_ms: durationMs,
  });
}

/**
 * Terrain quality tier change events
 */
export function trackTerrainQualityChange(
  previousTier: string,
  newTier: string,
  fps: number,
  reason: string
) {
  trackEvent("terrain_quality_change", {
    previous_tier: previousTier,
    new_tier: newTier,
    fps_at_change: fps,
    reason,
  });
}

/**
 * Terrain freeze events (performance too low, animation stopped)
 */
export function trackTerrainFreeze(
  tier: string,
  fps: number,
  frameCount: number
) {
  trackEvent("terrain_freeze", {
    tier_at_freeze: tier,
    fps_at_freeze: fps,
    total_frames: frameCount,
  });
}

/**
 * Terrain initialization events (which tier started)
 */
export function trackTerrainInit(
  initialTier: string,
  isMobile: boolean,
  reducedMotion: boolean,
  webglSupported: boolean
) {
  trackEvent("terrain_init", {
    initial_tier: initialTier,
    is_mobile: isMobile,
    reduced_motion: reducedMotion,
    webgl_supported: webglSupported,
  });
}
